import { useEffect, useRef } from 'react';
import { cleanLook, drawAvatar, type Look } from '../game/avatar';

// ─────────────────────────────────────────────────────────────────────────────
// A student's own fish (the one they designed for the ocean), swimming in
// place: their picture in the class game. A student who hasn't made one yet
// gets a plain fish in their own colour (from their id).
// ─────────────────────────────────────────────────────────────────────────────

const PLAIN = ['#ff9f43', '#5ad1ff', '#ff7eb6', '#7bd389', '#b57bff', '#ffd23d'];

export default function FishAvatar({ look, id, size = 64, excited = false }: { look: unknown; id: string; size?: number; excited?: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const g = c.getContext('2d')!;
    let h = 0; for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    const fish: Look = look ? cleanLook(look) : { ...cleanLook({}), bodyColor: PLAIN[h % PLAIN.length] };
    const seed = (h % 1000) / 100;
    let raf = 0, alive = true, visible = true;
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; });
    io.observe(c);
    const frame = (now: number) => {
      if (!alive) return;
      raf = requestAnimationFrame(frame);
      if (!visible) return;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const W = size * 1.5, H = size;
      if (c.width !== Math.round(W * dpr)) { c.width = Math.round(W * dpr); c.height = Math.round(H * dpr); }
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.clearRect(0, 0, W, H);
      const t = now / 1000;
      drawAvatar(g, fish, { x: W / 2 + (fish.gear?.trail ? size * 0.18 : 0), y: H / 2 + Math.sin(t * 2 + seed) * size * 0.05, size: size * 0.95, facing: 1, effort: excited ? 1 : 0.35, tilt: Math.sin(t * 1.3 + seed) * 0.08, t, seed });
    };
    raf = requestAnimationFrame(frame);
    return () => { alive = false; cancelAnimationFrame(raf); io.disconnect(); };
  }, [look, id, size, excited]);
  return <canvas ref={ref} className="fish-avatar" style={{ width: size * 1.5, height: size }} aria-hidden />;
}
